import { useState } from "react";
import { X, Loader2, SlidersHorizontal, Info } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useToastStore } from "../../../stores/toastStore";
import { getErrorMessage } from "../../../api/errorMap";
import {
  toggleMyWidget,
  type MyWidgetsResponse,
  type WidgetKey,
} from "./grcWidgets";
import { WIDGET_META } from "../DashboardConfig/dashboardConfigData";

interface CustomizeWidgetsPanelProps {
  open: boolean;
  onClose: () => void;
  widgetsResponse: MyWidgetsResponse | null;
  onWidgetsChanged: (updatedResponse: MyWidgetsResponse) => void;
}

export function CustomizeWidgetsPanel({
  open,
  onClose,
  widgetsResponse,
  onWidgetsChanged,
}: CustomizeWidgetsPanelProps) {
  const toastStore = useToastStore();
  const [savingMap, setSavingMap] = useState<Record<string, boolean>>({});

  const handleToggle = async (
    widgetKey: WidgetKey,
    orgUnitId: string,
    roleName: string,
    currentEnabled: boolean
  ) => {
    if (!widgetsResponse) return;

    const saveKey = `${roleName}-${orgUnitId}-${widgetKey}`;
    // Already in progress, ignore
    if (savingMap[saveKey]) return;

    // 1. Save previous state for rollback
    const previousResponse = { ...widgetsResponse };

    // 2. Perform optimistic update
    const nextEnabled = !currentEnabled;
    const updatedRoles = widgetsResponse.roles.map((role) => {
      if (role.roleName === roleName && role.orgUnitId === orgUnitId) {
        return {
          ...role,
          widgets: role.widgets.map((w) =>
            w.widget === widgetKey ? { ...w, enabled: nextEnabled } : w
          ),
        };
      }
      return role;
    });

    const optimisticResponse: MyWidgetsResponse = {
      ...widgetsResponse,
      roles: updatedRoles,
    };

    onWidgetsChanged(optimisticResponse);
    setSavingMap((prev) => ({ ...prev, [saveKey]: true }));

    // 3. Make API call
    try {
      const res = await toggleMyWidget(widgetKey, orgUnitId, nextEnabled);
      // Update with the actual server response if available
      if (res && res.roles) {
        onWidgetsChanged(res);
      }
    } catch (err: any) {
      // 4. Rollback on failure and show error
      onWidgetsChanged(previousResponse);
      const errMsg = getErrorMessage(err, `Failed to ${nextEnabled ? "enable" : "disable"} widget`);
      toastStore.showError("Update Failed", errMsg);
    } finally {
      setSavingMap((prev) => ({ ...prev, [saveKey]: false }));
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Scrim */}
          <motion.div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          {/* Slide-over Panel */}
          <motion.aside
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[500px] flex-col border-l border-border bg-card shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 380, damping: 38 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border p-4">
              <div className="flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500">
                  <SlidersHorizontal className="size-4" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-foreground">Customize Widgets</h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Show or hide widgets on your dashboard
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4">
              {!widgetsResponse || widgetsResponse.roles.length === 0 ? (
                <div className="flex h-32 flex-col items-center justify-center text-center">
                  <Info className="size-8 text-muted-foreground/60 mb-2" />
                  <span className="text-sm font-semibold text-muted-foreground">No Widgets Available</span>
                  <span className="text-xs text-muted-foreground/80 mt-1">There are no configurable widgets for your user profile.</span>
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  {widgetsResponse.roles.map((role) => {
                    const widgets = role.widgets ?? [];
                    if (widgets.length === 0) return null;

                    return (
                      <section key={`${role.roleName}-${role.orgUnitId}`} className="flex flex-col gap-3">
                        <div className="flex items-center justify-between border-b border-border pb-1.5">
                          <h3 className="text-xs font-bold tracking-wider text-foreground/60">
                            Role: {role.roleName}
                          </h3>
                        </div>

                        <div className="flex flex-col gap-2">
                          {widgets.map((w) => {
                            const meta = WIDGET_META[w.widget];
                            const title = meta?.title ?? w.widget.replace(/_/g, " ");
                            const description = meta?.description ?? "Toggle this dashboard widget";
                            const IconComponent = meta?.icon ?? Info;
                            const saveKey = `${role.roleName}-${role.orgUnitId}-${w.widget}`;
                            const isSaving = !!savingMap[saveKey];

                            return (
                              <div
                                key={w.widget}
                                className={`flex items-center justify-between gap-4 rounded-lg border p-3 transition-colors ${
                                  w.enabled
                                    ? "border-indigo-500/20 bg-indigo-500/[0.03]"
                                    : "border-border bg-background/50"
                                }`}
                              >
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                  <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-foreground/[0.06] text-muted-foreground">
                                    <IconComponent className="size-4" style={{ color: meta?.accent }} />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="block text-sm font-semibold text-foreground">
                                      {title}
                                    </span>
                                    <span className="mt-0.5 block text-xs text-muted-foreground leading-snug">
                                      {description}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex shrink-0 items-center">
                                  <ToggleSwitch
                                    on={w.enabled}
                                    saving={isSaving}
                                    onToggle={() =>
                                      handleToggle(
                                        w.widget,
                                        role.orgUnitId,
                                        role.roleName,
                                        w.enabled
                                      )
                                    }
                                    label={`Toggle ${title}`}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

/* -- Reusable toggle switch styled matching the app theme -- */
function ToggleSwitch({
  on,
  saving,
  disabled = false,
  onToggle,
  label,
}: {
  on: boolean;
  saving: boolean;
  disabled?: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={label}
      onClick={onToggle}
      disabled={saving || disabled}
      className={`relative inline-flex h-7 w-[64px] shrink-0 items-center rounded-full border transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 ${
        saving ? "disabled:cursor-wait" : "disabled:cursor-not-allowed disabled:opacity-60"
      } ${
        on
          ? "border-emerald-400/50 bg-emerald-500"
          : "border-border bg-foreground/15"
      }`}
    >
      {/* Status label inside */}
      <span
        className={`pointer-events-none absolute text-[10px] font-bold tracking-wide transition-all duration-200 ${
          on ? "left-2.5 text-white" : "right-2.5 text-muted-foreground/80"
        }`}
      >
        {on ? "On" : "Off"}
      </span>
      {/* Sliding knob */}
      <span
        className={`pointer-events-none absolute left-1 flex size-5 items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-200 ${
          on ? "translate-x-[36px]" : "translate-x-0"
        }`}
      >
        {saving && <Loader2 className="size-3 animate-spin text-foreground/40" />}
      </span>
    </button>
  );
}
